import React, { useState, useEffect } from 'react';
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import './AjoutMembre.css';

type Props = { isAdmin: boolean };

function AjoutMembre({ isAdmin }: Props) {
  const navigate = useNavigate();
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoURL, setPhotoURL] = useState<string>('');

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    dateNaissance: '',
    mail: '',
    adresse: '',
    codePostal: '',
    ville: '',
    telephone: '',
    loge: '',
    grades: '',
    fonction: '',

    // ✅ par défaut
    administrateur: 'non',
    statut: 'Valide',
  });

  const [error, setError] = useState('');
  const [authError, setAuthError] = useState('');
  const [loading, setLoading] = useState(false);

  const [fonctions, setFonctions] = useState<string[]>([]);
  const [statuts, setStatuts] = useState<string[]>([]);
  const [loges, setLoges] = useState<string[]>([]);
  const [grades, setGrades] = useState<string[]>([]);

  const [showExistingMemberModal, setShowExistingMemberModal] = useState(false);
  const [existingMemberData, setExistingMemberData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      const db = getFirestore();

      const fonctionsSnapshot = await getDocs(collection(db, 'Fonctions'));
      setFonctions(fonctionsSnapshot.docs.map((d) => d.id));

      const gradesSnapshot = await getDocs(collection(db, 'Grades'));
      setGrades(gradesSnapshot.docs.map((d) => d.id));

      const statutsSnapshot = await getDocs(collection(db, 'Statut'));
      setStatuts(statutsSnapshot.docs.map((d) => d.id));

      const logesSnapshot = await getDocs(collection(db, 'Loges'));
      setLoges(logesSnapshot.docs.map((d) => d.id));
    };

    fetchData();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPhoto(file);

      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') setPhotoURL(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhotoIfNeeded = async (existingPhoto?: string) => {
    if (!photo) return existingPhoto || 'Aucune photo';
    const storage = getStorage();
    const timestamp = Date.now();
    const photoRef = ref(storage, `membres/${timestamp}_${photo.name}`);
    await uploadBytes(photoRef, photo);
    return await getDownloadURL(photoRef);
  };

  const createFirebaseAuthUser = async (email: string): Promise<boolean> => {
    try {
      const auth = getAuth();
      await createUserWithEmailAndPassword(auth, email, '123456');
      return true;
    } catch (err: any) {
      if (err?.code === 'auth/email-already-in-use') {
        // Compte déjà existant => OK
        setAuthError('');
        return true;
      }
      setAuthError(err?.message || 'Erreur Auth');
      return false;
    }
  };

  // ✅ Vérif mail -> modal si membre existe
  const handleEmailBlur = async () => {
    const email = formData.mail.trim().toLowerCase();
    if (!email) return;

    try {
      const db = getFirestore();
      const q = query(collection(db, 'Membres'), where('mail', '==', email));
      const snap = await getDocs(q);

      if (!snap.empty) {
        setExistingMemberData(snap.docs[0].data());
        setShowExistingMemberModal(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  // ✅ Charger données existantes, mais forcer choix loge + fonction
  const loadExistingMemberData = () => {
    if (!existingMemberData) return;

    setFormData({
      nom: existingMemberData.nom || '',
      prenom: existingMemberData.prenom || '',
      dateNaissance: existingMemberData.dateNaissance || '',
      mail: existingMemberData.mail || '',
      adresse: existingMemberData.adresse || '',
      codePostal: existingMemberData.codePostal || '',
      ville: existingMemberData.ville || '',
      telephone: existingMemberData.telephone || '',
      loge: '',
      grades: existingMemberData.grades || '',
      fonction: '',

      // ✅ si pas admin => forcé ; si admin => on garde les valeurs existantes par défaut
      administrateur: isAdmin ? (existingMemberData.administrateur || 'non') : 'non',
      statut: isAdmin ? (existingMemberData.statut || 'Valide') : 'Valide',
    });

    if (existingMemberData.photo) setPhotoURL(existingMemberData.photo);

    setShowExistingMemberModal(false);
  };

  const continueWithNewMember = () => {
    setShowExistingMemberModal(false);
    setExistingMemberData(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const email = formData.mail.trim().toLowerCase();

    if (!formData.nom || !formData.prenom || !email || !formData.loge || !formData.fonction) {
      setError('Veuillez remplir tous les champs obligatoires (nom, prénom, email, loge, fonction).');
      return;
    }

    // ✅ Forcer statut/admin si pas admin
    const adminValue = isAdmin ? (formData.administrateur || 'non') : 'non';
    const statutValue = isAdmin ? (formData.statut || 'Valide') : 'Valide';

    try {
      setLoading(true);
      setError('');
      setAuthError('');

      const db = getFirestore();

      // Chercher membre existant par mail
      const q = query(collection(db, 'Membres'), where('mail', '==', email));
      const snap = await getDocs(q);

      if (!snap.empty) {
        // ✅ MEMBRE EXISTANT -> update ciblé
        const d = snap.docs[0];
        const membreRef = doc(db, 'Membres', d.id);
        const existingData = d.data();

        const photoUrlToSave = await uploadPhotoIfNeeded(existingData.photo);

        await updateDoc(membreRef, {
          photo: photoUrlToSave,

          // ✅ multi-loges + rôle par loge
          loges: arrayUnion(formData.loge),
          [`fonctionsParLoge.${formData.loge}`]: formData.fonction,

          // Profil
          nom: formData.nom,
          prenom: formData.prenom,
          adresse: formData.adresse,
          codePostal: formData.codePostal,
          ville: formData.ville,
          telephone: formData.telephone,
          dateNaissance: formData.dateNaissance,
          grades: formData.grades,

          // ✅ ces 2 champs : admin only
          administrateur: adminValue,
          statut: statutValue,

          dateModification: serverTimestamp(),
        });

        navigate('/mapage/membres');
        return;
      }

      // ✅ NOUVEAU MEMBRE
      const authCreated = await createFirebaseAuthUser(email);
      if (!authCreated) return;

      const photoUrlToSave = await uploadPhotoIfNeeded('Aucune photo');
      const membreId = email.replace(/[@.]/g, '_');

      await setDoc(doc(db, 'Membres', membreId), {
        id: membreId,
        photo: photoUrlToSave,

        nom: formData.nom,
        prenom: formData.prenom,
        adresse: formData.adresse,
        codePostal: formData.codePostal,
        ville: formData.ville,
        telephone: formData.telephone,
        dateNaissance: formData.dateNaissance,
        mail: email,
        grades: formData.grades,

        // ✅ multi-loges
        loges: [formData.loge],
        fonctionsParLoge: { [formData.loge]: formData.fonction },

        // ✅ admin/statut
        administrateur: adminValue,
        statut: statutValue,

        dateCreation: serverTimestamp(),
        dateModification: serverTimestamp(),
      });

      navigate('/mapage/membres');
    } catch (err) {
      console.error(err);
      setError("Une erreur est survenue lors de l'ajout du membre.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ajout-membre-container">
      <h2>Ajouter un nouveau membre</h2>

      {error && <div className="error-message">{error}</div>}
      {authError && <div className="error-message">{authError}</div>}

      {showExistingMemberModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>⚠️ Membre déjà existant</h3>
            <p className="modal-message">
              Un membre avec cette adresse email existe déjà. Voulez-vous charger ses infos et l’ajouter à une nouvelle loge ?
            </p>

            <div className="existing-member-info">
              <p><strong>Nom:</strong> {existingMemberData?.nom} {existingMemberData?.prenom}</p>
              <p><strong>Loge(s):</strong> {Array.isArray(existingMemberData?.loges) ? existingMemberData.loges.join(', ') : existingMemberData?.loge}</p>
              <p><strong>Grade:</strong> {existingMemberData?.grades}</p>
            </div>

            <div className="modal-buttons">
              <button type="button" onClick={loadExistingMemberData} className="btn-yes">
                Oui, charger
              </button>
              <button type="button" onClick={continueWithNewMember} className="btn-no">
                Non, nouveau membre
              </button>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="ajout-membre-form">
        <div className="form-columns">
          <div className="form-column">
            <div className="form-group">
              <label htmlFor="photo">Photo de profil:</label>
              <input type="file" id="photo" accept="image/*" onChange={handlePhotoChange} className="form-input" />
              {photoURL && (
                <div className="photo-preview">
                  <img src={photoURL} alt="Aperçu" />
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="nom">Nom: <span className="required">*</span></label>
              <input type="text" id="nom" name="nom" value={formData.nom} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="prenom">Prénom: <span className="required">*</span></label>
              <input type="text" id="prenom" name="prenom" value={formData.prenom} onChange={handleChange} required />
            </div>

            <div className="form-group">
              <label htmlFor="mail">Email: <span className="required">*</span></label>
              <input type="email" id="mail" name="mail" value={formData.mail} onChange={handleChange} onBlur={handleEmailBlur} required />
            </div>

            <div className="form-group">
              <label htmlFor="telephone">Téléphone:</label>
              <input type="tel" id="telephone" name="telephone" value={formData.telephone} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="dateNaissance">Date de naissance:</label>
              <input type="date" id="dateNaissance" name="dateNaissance" value={formData.dateNaissance} onChange={handleChange} />
            </div>
          </div>

          <div className="form-column">
            <div className="form-group">
              <label htmlFor="adresse">Adresse:</label>
              <input type="text" id="adresse" name="adresse" value={formData.adresse} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="codePostal">Code Postal:</label>
              <input type="text" id="codePostal" name="codePostal" value={formData.codePostal} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="ville">Ville:</label>
              <input type="text" id="ville" name="ville" value={formData.ville} onChange={handleChange} />
            </div>

            <div className="form-group">
              <label htmlFor="loge">Loge: <span className="required">*</span></label>
              <select id="loge" name="loge" value={formData.loge} onChange={handleChange} required>
                <option value="">Sélectionnez une loge</option>
                {loges.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="grades">Grade:</label>
              <select id="grades" name="grades" value={formData.grades} onChange={handleChange}>
                <option value="">Sélectionnez un grade</option>
                {grades.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="fonction">Fonction pour cette loge: <span className="required">*</span></label>
              <select id="fonction" name="fonction" value={formData.fonction} onChange={handleChange} required>
                <option value="">Sélectionnez une fonction</option>
                {fonctions.map((f) => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>

            {/* ✅ Admin only */}
            {isAdmin && (
              <>
                <div className="form-group">
                  <label htmlFor="administrateur">Administrateur:</label>
                  <select
                    id="administrateur"
                    name="administrateur"
                    value={formData.administrateur}
                    onChange={handleChange}
                    required
                  >
                    <option value="non">Non</option>
                    <option value="oui">Oui</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="statut">Statut:</label>
                  <select
                    id="statut"
                    name="statut"
                    value={formData.statut}
                    onChange={handleChange}
                    required
                  >
                    {statuts.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/mapage/membres')} className="cancel-btn" disabled={loading}>
            Annuler
          </button>
          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Ajout en cours...' : 'Ajouter le membre'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AjoutMembre;
